import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';

// M5.14: Chronicle theme editor. JSON-pretty-printed
// textarea editing the per-team theme (and Ollama URL /
// model override + chronicle_enabled toggle). Save sends
// a PATCH to /api/admin/teams/:id/chronicle-config; the
// api validates the shape and returns the resolved row.
//
// The editor is intentionally a free-form JSON textarea
// rather than a per-field form. The theme shape is
// documented in the placeholder text below and the admin
// is expected to be comfortable editing JSON — a per-field
// form would add ~150 LOC for the same outcome and would
// need to learn how to round-trip a `characters` map
// (which is the only nested object). A textarea is the
// smallest UI that does the job.
//
// Reset-to-default: replaces the textarea content with
// the canonical default theme shipped in migration 0036.
// The "default" is the same JS object the api's
// DEFAULT_CHRONICLE_THEME constant — see
// apps/api/src/chronicles/generator.ts.

interface ThemeShape {
  name: string;
  setting: string;
  voice: string;
  max_words: number;
  characters?: Record<string, string>;
}

interface ChronicleConfigResponse {
  theme: ThemeShape;
  ollama_url: string | null;
  ollama_model: string | null;
  chronicle_enabled: boolean;
  // M5.14 page age cap. The api returns the RESOLVED
  // window (per-team override → env default → 24h), so
  // the input shows the effective value the admin will
  // see on the wall TV. Clearing the input sends
  // window_hours=null, which clears the per-team override
  // and falls back to the env default.
  window_hours: number;
  // M5.x loot drop chance (ADR-0022). Per-team override
  // of the quest-completion loot rate. null = use the
  // env default (LOOT_DROP_CHANCE, 0.05 by default). The
  // api returns the raw configured value (NOT the resolved
  // effective) so the admin UI mirrors what they set —
  // same shape as the env-var fallback semantics.
  loot_drop_chance: number | null;
  // M5.x Surface-2 event-driven loot chance (ADR-0023).
  // JSONB keyed by ingestor_event.type, values 0..1. null
  // = operator cleared (api stores '{}'). The form's
  // textarea parses to an object on save; the api
  // validates per-key 0..1 + known event types.
  loot_event_chance: Record<string, number> | null;
  // Kokoro voice id for the read-aloud TTS (e.g. 'af_heart').
  // The api returns the resolved value (default af_heart when
  // the per-team override is null).
  tts_voice: string;
}

interface Props {
  teamId: string;
}

// Kokoro voices grouped by accent + gender for the read-aloud
// voice picker. Kept in sync with the api's KOKORO_VOICES list
// (apps/api/src/api/public-board.routes.ts) — if kokoro-js adds
// voices, update both sides.
const KOKORO_VOICE_GROUPS = {
  af: ['af_heart', 'af_alloy', 'af_aoede', 'af_bella', 'af_jessica', 'af_kore', 'af_nicole', 'af_nova', 'af_river', 'af_sarah', 'af_sky'],
  am: ['am_adam', 'am_echo', 'am_eric', 'am_fenrir', 'am_liam', 'am_michael', 'am_onyx', 'am_puck', 'am_santa'],
  bf: ['bf_emma', 'bf_isabella', 'bf_alice', 'bf_lily'],
  bm: ['bm_george', 'bm_lewis', 'bm_daniel', 'bm_fable'],
} as const;

const PLACEHOLDER_DEFAULT_THEME: ThemeShape = {
  name: 'Forbury Fellowship of the Sprint',
  setting: 'A fellowship of engineers on an unending quest to vanquish bugs, ship features, and hold the line against the backlog of Mount Yolo. The Fellowship rides between the citadels of Forbury Development and Forbury Product, where each pull request is a spell, each Jira ticket is an artifact, and each closed bug is a battle won.',
  voice: 'epic, third-person, literary fantasy in the spirit of Tolkien',
  max_words: 120,
  characters: {
    engineer: 'a fellow of the Fellowship',
    pull_request: 'spell',
    merged_pull_request: 'vanquished the bug',
    issue_transition: 'moved the artifact',
    review: 'council of review',
    approved_review: 'gave their blessing',
    kudos: 'bestowed fellowship honours',
  },
};

export function ChronicleThemeEditor({ teamId }: Props) {
  const cfg = useQuery({
    queryKey: ['chronicle-config', teamId],
    queryFn: () =>
      api<ChronicleConfigResponse>(`/api/admin/teams/${teamId}/chronicle-config`),
    enabled: !!teamId,
  });

  // Local state for the textarea and the override fields.
  // We seed from the query once it loads; user edits live
  // in local state until Save.
  const [text, setText] = useState<string>('');
  const [ollamaUrl, setOllamaUrl] = useState<string>('');
  const [ollamaModel, setOllamaModel] = useState<string>('');
  const [enabled, setEnabled] = useState<boolean>(true);
  // M5.14 page age cap. The api echoes back the RESOLVED
  // window (per-team override → env default → 24h), so
  // the input starts with the effective value, not null.
  // The input is empty when the per-team override is null
  // and the admin wants to send `null` (clear override
  // + use env default); the Save handler distinguishes
  // empty-input (→ null in the body) from non-empty
  // (→ that number in the body).
  const [windowHours, setWindowHours] = useState<string>('');
  // M5.x loot drop chance (ADR-0022). Same empty-as-null
  // pattern as windowHours: empty input sends
  // loot_drop_chance=null to the api, which clears the
  // per-team override and falls back to env default.
  const [lootChance, setLootChance] = useState<string>('');
  // M5.x Surface-2 event-driven loot chance (ADR-0023).
  // The api returns a JSONB object keyed by event type;
  // the form serialises it back as JSON in the textarea.
  // Empty JSON `{}` means "no per-event-type drops for
  // this team" — same effective state as null. We keep
  // it as a string in form state so empty-as-null
  // mirrors the rest of the form's semantics.
  const [lootEventChance, setLootEventChance] = useState<string>('');
  // Kokoro TTS voice for the read-aloud feature. The api
  // returns the resolved voice (default af_heart); the
  // select starts with it.
  const [ttsVoice, setTtsVoice] = useState<string>('af_heart');
  const [seeded, setSeeded] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Seed local state from the server once per team. The
  // seedRef guards against the user's local edits being
  // overwritten if the query refetches (e.g. background
  // invalidation after another admin saved).
  useEffect(() => {
    if (!cfg.data || seeded) return;
    setText(JSON.stringify(cfg.data.theme, null, 2));
    setOllamaUrl(cfg.data.ollama_url ?? '');
    setOllamaModel(cfg.data.ollama_model ?? '');
    setEnabled(cfg.data.chronicle_enabled);
    // M5.14: the api returns the resolved window as a
    // number (24 by default). Pre-fill the input with the
    // string version so the admin sees what they're
    // actually getting.
    setWindowHours(String(cfg.data.window_hours));
    // M5.x loot drop chance (ADR-0022). The api returns
    // the raw configured value (null = env default). When
    // null, show an empty input + the helper text "blank =
    // use env default". When a number, pre-fill with that
    // number's string form (step="0.005" supports 3-decimal
    // entries).
    setLootChance(
      cfg.data.loot_drop_chance === null || cfg.data.loot_drop_chance === undefined
        ? ''
        : String(cfg.data.loot_drop_chance),
    );
    // M5.x Surface-2 (ADR-0023). The api returns
    // null when the operator cleared the JSONB; we
    // serialise as empty-string in the form so the
    // textarea stays clean. Non-null maps to JSON.
    setLootEventChance(
      cfg.data.loot_event_chance === null || cfg.data.loot_event_chance === undefined
        ? ''
        : JSON.stringify(cfg.data.loot_event_chance, null, 2),
    );
    setTtsVoice(cfg.data.tts_voice ?? 'af_heart');
    setSeeded(true);
  }, [cfg.data, seeded]);

  // Reset seed when team changes (so we re-seed from the
  // new team's config, not from the previous team's).
  useEffect(() => { setSeeded(false); }, [teamId]);

  // Parse the textarea so we can show "invalid JSON" inline
  // rather than failing at save time. The save mutation
  // does the canonical parse + PATCH.
  const parsedTheme = useMemo<{ ok: true; value: ThemeShape } | { ok: false; error: string }>(() => {
    if (!text.trim()) return { ok: false, error: 'Theme is empty.' };
    try {
      const obj = JSON.parse(text);
      if (typeof obj !== 'object' || obj === null) {
        return { ok: false, error: 'Theme must be a JSON object.' };
      }
      return { ok: true, value: obj as ThemeShape };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Invalid JSON.' };
    }
  }, [text]);

  const save = useMutation({
    mutationFn: () => {
      if (!parsedTheme.ok) throw new Error(parsedTheme.error);
      // M5.14 page age cap: parse the input. Empty
      // string = clear the per-team override (api falls
      // back to env default). A number in [1, 168]
      // sets it. We validate range locally so the Save
      // button is disabled on invalid input — the api
      // also validates, this is just client-side UX.
      const trimmedWindow = windowHours.trim();
      let windowHoursBody: number | null | undefined;
      if (trimmedWindow === '') {
        windowHoursBody = null;
      } else {
        const n = Number(trimmedWindow);
        if (Number.isFinite(n) && Number.isInteger(n) && n >= 1 && n <= 168) {
          windowHoursBody = n;
        } else {
          throw new Error('Window hours must be 1-168 or empty (clear override).');
        }
      }

      // M5.x loot drop chance (ADR-0022). Same empty
      // semantics as window_hours. The api accepts
      // fractional values 0..1; the column is NUMERIC(4,3)
      // so up to 3 decimals of precision. Empty = use
      // the env default (LOOT_DROP_CHANCE).
      const trimmedLoot = lootChance.trim();
      let lootChanceBody: number | null | undefined;
      if (trimmedLoot === '') {
        lootChanceBody = null;
      } else {
        const n = Number(trimmedLoot);
        // Non-finite (e.g. typing in progress) or out-of-range
        // values cause Save to throw with a useful message.
        // Fractions like "0.025" pass; integers like "0" or
        // "1" pass; anything outside [0, 1] or non-numeric
        // errors here.
        if (Number.isFinite(n) && n >= 0 && n <= 1) {
          lootChanceBody = n;
        } else {
          throw new Error('Loot drop chance must be 0-1 or empty (clear override).');
        }
      }

      // M5.x Surface-2 (ADR-0023). Empty textarea =
      // clear (loot_event_chance=null). Non-empty must
      // be valid JSON: an object with keys from the
      // event-type enum and values 0..1. We parse
      // client-side for friendly error messages;
      // the api validates the shape server-side and
      // returns 400 + invalid_loot_event_chance with
      // the offending key. Same client-side UX
      // philosophy as the parsedTheme check above.
      const trimmedEventChance = lootEventChance.trim();
      let lootEventChanceBody: Record<string, number> | null | undefined;
      if (trimmedEventChance === '' || trimmedEventChance === '{}') {
        lootEventChanceBody = null;
      } else {
        let parsed: unknown;
        try {
          parsed = JSON.parse(trimmedEventChance);
        } catch (e) {
          throw new Error(
            'Loot event chance: not valid JSON. Use {} to disable, or an object like {"issue.commented": 0.02, "pr.closed": 0.05}.',
          );
        }
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error(
            'Loot event chance must be a JSON object {eventType: 0..1} or {} to disable.',
          );
        }
        // Cheap client-side range check; the api revalidates.
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
          if (typeof v !== 'number' || !Number.isFinite(v) || (v as number) < 0 || (v as number) > 1) {
            throw new Error(
              `Loot event chance.${k} must be a number 0-1 (got ${JSON.stringify(v)}).`,
            );
          }
        }
        lootEventChanceBody = parsed as Record<string, number>;
      }
      return api<ChronicleConfigResponse>(
        `/api/admin/teams/${teamId}/chronicle-config`,
        {
          method: 'PATCH',
          body: {
            theme: parsedTheme.value,
            // Empty string → null (clear override). The api
            // distinguishes "leave alone" (omitted) from
            // "clear override" (null) — empty string in
            // the form is the operator's signal to clear.
            ollama_url: ollamaUrl.trim() === '' ? null : ollamaUrl.trim(),
            ollama_model: ollamaModel.trim() === '' ? null : ollamaModel.trim(),
            chronicle_enabled: enabled,
            window_hours: windowHoursBody,
            loot_drop_chance: lootChanceBody,
            loot_event_chance: lootEventChanceBody,
            chronicle_tts_voice: ttsVoice,
          },
        },
      );
    },
    onSuccess: (data) => {
      setOk('Saved.');
      setErr(null);
      // Re-seed from the server's resolved values (catches
      // any clamping the api did — e.g. a max_words
      // fractional that was floored to an integer).
      setText(JSON.stringify(data.theme, null, 2));
      setOllamaUrl(data.ollama_url ?? '');
      setOllamaModel(data.ollama_model ?? '');
      setEnabled(data.chronicle_enabled);
      // M5.14: echo back the resolved window so the
      // input shows what the server stored (catches
      // out-of-range clamps if the api's range ever
      // drifts from the form's).
      setWindowHours(String(data.window_hours));
      // M5.x loot drop chance (ADR-0022). Echo back the
      // raw configured value so the input mirrors the
      // server's view (null = "using env default", empty
      // input + helper text). The api returns numbers as
      // their JS Number form; the empty string is for
      // null overrides.
      setLootChance(
        data.loot_drop_chance === null || data.loot_drop_chance === undefined
          ? ''
          : String(data.loot_drop_chance),
      );
      // M5.x Surface-2 (ADR-0023). Echo back the
      // JSONB chance so the textarea mirrors the
      // server's view. We pretty-print with 2-space
      // indent on load; saving without re-indenting
      // still parses fine (the api just round-trips
      // whatever JSON the form sends).
      setLootEventChance(
        data.loot_event_chance === null || data.loot_event_chance === undefined
          ? ''
          : JSON.stringify(data.loot_event_chance, null, 2),
      );
      setTtsVoice(data.tts_voice ?? 'af_heart');
    },
    onError: (e) => {
      setOk(null);
      setErr(e instanceof ApiError ? e.message : (e instanceof Error ? e.message : 'Save failed.'));
    },
  });

  const resetToDefault = () => {
    setText(JSON.stringify(PLACEHOLDER_DEFAULT_THEME, null, 2));
    setErr(null);
    setOk(null);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-muted">
        The Chronicles is a per-team hour-tick of fantasy prose
        about the team's most recent activity. The theme controls
        voice, setting, and character translations. The Ollama URL
        and model override the env defaults for this team only.
      </p>

      {err ? <p className="text-sm text-accent-danger">{err}</p> : null}
      {ok ? <p className="text-sm text-accent-success">{ok}</p> : null}

      <div className="space-y-2">
        <label className="label" htmlFor="chronicle-theme-json">
          Theme (JSON)
        </label>
        <textarea
          id="chronicle-theme-json"
          className="input h-96 font-mono text-xs"
          spellCheck={false}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={JSON.stringify(PLACEHOLDER_DEFAULT_THEME, null, 2)}
        />
        {parsedTheme.ok ? (
          <p className="text-xs text-ink-dim">
            Valid JSON. {describeTheme(parsedTheme.value)}.
          </p>
        ) : (
          <p className="text-xs text-accent-danger">{parsedTheme.error}</p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="label" htmlFor="chronicle-ollama-url">
            Ollama URL override (per-team)
          </label>
          <input
            id="chronicle-ollama-url"
            className="input"
            type="text"
            placeholder="http://host.docker.internal:11434"
            value={ollamaUrl}
            onChange={(e) => setOllamaUrl(e.target.value)}
          />
          <p className="text-xs text-ink-dim">
            Leave blank to use the env default
            (<code>CHRONICLE_OLLAMA_URL</code>). Useful when a team
            wants to test a different model endpoint.
          </p>
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="chronicle-ollama-model">
            Ollama model override
          </label>
          <input
            id="chronicle-ollama-model"
            className="input"
            type="text"
            placeholder="minimax-m3:cloud"
            value={ollamaModel}
            onChange={(e) => setOllamaModel(e.target.value)}
          />
          <p className="text-xs text-ink-dim">
            Leave blank to use the env default
            (<code>CHRONICLE_OLLAMA_MODEL</code>). Must be a
            model the host's Ollama has access to.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="chronicle-enabled"
          type="checkbox"
          className="h-4 w-4"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <label htmlFor="chronicle-enabled" className="text-sm">
          Generate chapters for this team (off = silent hour)
        </label>
      </div>

      {/*
        TTS voice picker (Kokoro). The /chronicles page can
        read each paragraph aloud. The voice is per-team.
        Grouped by accent + gender to make scanning easy.
        Changing the voice here affects the next synthesis
        (the LRU cache keys on chapter id + text, not voice,
        so the operator hears the new voice on the first
        re-read of each paragraph — or any new chapter).
      */}
      <div className="space-y-1">
        <label className="label" htmlFor="chronicle-tts-voice">
          Read-aloud voice (Kokoro)
        </label>
        <select
          id="chronicle-tts-voice"
          className="input max-w-xs"
          value={ttsVoice}
          onChange={(e) => setTtsVoice(e.target.value)}
        >
          <optgroup label="American — female">
            {KOKORO_VOICE_GROUPS.af.map((v) => <option key={v} value={v}>{v}</option>)}
          </optgroup>
          <optgroup label="American — male">
            {KOKORO_VOICE_GROUPS.am.map((v) => <option key={v} value={v}>{v}</option>)}
          </optgroup>
          <optgroup label="British — female">
            {KOKORO_VOICE_GROUPS.bf.map((v) => <option key={v} value={v}>{v}</option>)}
          </optgroup>
          <optgroup label="British — male">
            {KOKORO_VOICE_GROUPS.bm.map((v) => <option key={v} value={v}>{v}</option>)}
          </optgroup>
        </select>
        <p className="text-xs text-ink-dim">
          The wall-TV player picks this voice when reading a paragraph aloud. Changes apply to the next paragraph that hasn't been cached yet.
        </p>
      </div>

      {/*
        M5.14 page age cap. The /chronicles page renders
        chapters whose hour_utc is within the last N hours.
        Default is 24h (env var CHRONICLE_WINDOW_HOURS);
        this input is a per-team override. Empty input
        clears the override and falls back to the env
        default. Range 1-168 (1 hour..1 week). Lower
        than 1 would flip the page to empty mid-hour;
        higher than 168 matches the existing
        live_feed_timeout_minutes upper bound (10080 min
        = 168 hours) so the wall TV's two surfaces stay
        consistent in their max-config ceiling.
      */}
      <div className="space-y-1">
        <label className="label" htmlFor="chronicle-window-hours">
          Page window (hours, blank = env default 24)
        </label>
        <div className="flex items-center gap-3">
          <input
            id="chronicle-window-hours"
            className="input w-24"
            type="number"
            min={1}
            max={168}
            step={1}
            value={windowHours}
            placeholder="24"
            onChange={(e) => setWindowHours(e.target.value)}
          />
          <button
            type="button"
            className="btn text-xs"
            onClick={() => setWindowHours('')}
            title="Clear the per-team override and use the env default (CHRONICLE_WINDOW_HOURS, or 24 if unset)."
          >
            Use env default
          </button>
        </div>
        <p className="text-xs text-ink-dim">
          A chapter expires when its hour_utc is older than this window. 1 hour is hyper-fresh; 24h (the default) keeps Friday-evening prose on screen Saturday morning but expires before Sunday; 168 (1 week) is the cap. The page renders up to 3 chapters that fall within the window; if fewer exist, you see fewer.
        </p>
      </div>

      {/*
        M5.x Loot drop chance (ADR-0022). Per-team
        override of the quest-completion loot roll rate.
        Same empty-as-null + 'Use env default' pattern as
        the window-hours field above. Defaults: step=0.005
        so 3-decimal entries (e.g. 0.025) submit cleanly.
        Range 0-1 enforced client-side AND server-side (the
        api rejects out-of-range with 400 + invalid_loot_drop_chance).
      */}
      <div className="space-y-1">
        <label className="label" htmlFor="chronicle-loot-chance">
          Loot drop chance (0–1, blank = env default 0.05)
        </label>
        <div className="flex items-center gap-3">
          <input
            id="chronicle-loot-chance"
            className="input w-32"
            type="number"
            min={0}
            max={1}
            step={0.005}
            value={lootChance}
            placeholder="0.05"
            onChange={(e) => setLootChance(e.target.value)}
          />
          <button
            type="button"
            className="btn text-xs"
            onClick={() => setLootChance('')}
            title="Clear the per-team override and use the env default (LOOT_DROP_CHANCE, or 0.05 if unset)."
          >
            Use env default
          </button>
        </div>
        <p className="text-xs text-ink-dim">
          Probability that a quest completion rolls a loot badge (Shiny PR / Lucky Merge / Phoenix Feather / The Holy Grail — weighted). 0 = drops disabled for this team; 1 = every completion rolls. Setting per-team overrides the env default; blank falls back to it.
        </p>
      </div>

      {/*
        M5.x Surface-2 (ADR-0023). Per-(team × event-type)
        loot drop chance as JSON. Keys are
        ingestor_event.type values; values are numbers
        0..1. Empty JSON {} clears the team's chance (the
        same effective state as null — the api
        normalises). The form parses client-side for
        friendly error messages; the api validates again
        and returns 400 + invalid_loot_event_chance with
        the offending key.

        Three event types are seeded by the admin form
        for typical use: issue.commented (e.g. "Lucky
        Comment"), pr.closed (e.g. "Holy Grail"), kudos
        .given. The full enum is enforced server-side
        (see LOOT_EVENT_TYPES in scoring/loot-and-classes
        .ts); the form's textarea is open to all keys
        so the operator can paste a complete config
        from elsewhere.
      */}
      <div className="space-y-1">
        <label className="label" htmlFor="chronicle-loot-event-chance">
          Loot event chance (Surface 2, JSON; blank = no event-driven drops)
        </label>
        <textarea
          id="chronicle-loot-event-chance"
          className="input font-mono text-xs"
          rows={5}
          value={lootEventChance}
          placeholder={`{
  "issue.commented": 0.02,
  "pr.closed": 0.05,
  "kudos.given": 0.01
}`}
          onChange={(e) => setLootEventChance(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn text-xs"
            onClick={() => setLootEventChance('{}')}
            title='Disable event-driven drops for this team. Same as deleting all keys; sends "{}" to the api.'
          >
            Disable all
          </button>
          <p className="text-xs text-ink-dim">
            Per-(team × event-type) chance for the M5.x Surface-2 loot pool (badges with non-null <code>loot_event_type</code>). Allowed event types: <code>pr.closed</code>, <code>issue.transition</code>, <code>pr.review.submitted</code>, <code>kudos.given</code>, <code>issue.created</code>, <code>pr.opened</code>, <code>issue.commented</code>. Missing keys fall back to env default (LOOT_EVENT_DROP_CHANCE_DEFAULT).
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button
            type="button"
            className="btn"
            onClick={resetToDefault}
            disabled={cfg.isLoading}
          >
            Reset to default theme
          </button>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => save.mutate()}
          disabled={save.isPending || !parsedTheme.ok || cfg.isLoading}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function describeTheme(t: ThemeShape): string {
  const fields = [
    `name="${t.name}"`,
    `setting=${t.setting.length} chars`,
    `voice="${t.voice}"`,
    `max_words=${t.max_words}`,
    `characters=${Object.keys(t.characters ?? {}).length} entries`,
  ];
  return fields.join(' · ');
}
